import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowCounterClockwise, FloppyDisk, Lock } from "@phosphor-icons/react";

export default function RoleMatrixTab() {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/permissions/matrix");
      setData(d);
      setMatrix(d.matrix || {});
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const roles = useMemo(() => data?.roles || [], [data]);

  const toggle = (role, action) => {
    if (role === "super_admin") return;
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [action]: !m[role]?.[action] } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/permissions/matrix", { matrix });
      setData(d); setMatrix(d.matrix || {}); setDirty(false);
      toast.success("Role matrix saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/permissions/matrix/reset");
      setData(d); setMatrix(d.matrix || {}); setDirty(false);
      toast.success("Matrix reset to defaults");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="py-12 text-center text-sm text-slate-400" data-testid="matrix-loading">Loading matrix…</div>;

  return (
    <div className="mt-4 space-y-6" data-testid="role-matrix-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={!dirty || busy} className="btn-primary-blue" data-testid="matrix-save-btn">
          <FloppyDisk size={16} className="mr-1.5" /> {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={busy} data-testid="matrix-reset-btn">
              <ArrowCounterClockwise size={16} className="mr-1.5" /> Reset to defaults
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="matrix-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset the role matrix?</AlertDialogTitle>
              <AlertDialogDescription>
                Every role returns to its default permissions. Per-user overrides are NOT affected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="matrix-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={reset} data-testid="matrix-reset-confirm">Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {data.meta?.updated_at && (
          <span className="text-xs text-slate-400" data-testid="matrix-meta">
            Last edited {new Date(data.meta.updated_at).toLocaleString()} by {data.meta.updated_by}
          </span>
        )}
      </div>

      {(data.modules || []).map((mod) => (
        <div key={mod.key} className="rounded-xl border border-slate-200 bg-white" data-testid={`matrix-module-${mod.key}`}>
          <div className="border-b border-slate-100 px-4 py-3">
            <span className="font-display text-sm font-bold text-slate-800">{mod.label}</span>
            <span className="ml-2 font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">
              {mod.actions.length} action{mod.actions.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="sticky left-0 z-10 min-w-[240px] bg-white px-4 py-2 text-left font-medium text-slate-500">Action</th>
                  {roles.map((r) => (
                    <th key={r.key} className="px-2 py-2 text-center align-bottom">
                      <div className="mx-auto flex flex-col items-center gap-0.5">
                        <span className="whitespace-nowrap font-mono-alt text-[9px] uppercase tracking-wider text-slate-500">
                          {r.label.split(" ")[0]}
                        </span>
                        <span className="whitespace-nowrap font-mono-alt text-[9px] uppercase tracking-wider text-slate-400">
                          {r.label.split(" ").slice(1).join(" ") || "\u00A0"}
                        </span>
                        {r.locked && <Lock size={10} className="text-amber-500" />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mod.actions.map((a) => (
                  <tr key={a.key} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2 text-slate-700">
                      {a.label}
                      <div className="font-mono-alt text-[9px] text-slate-300">{a.key}</div>
                    </td>
                    {roles.map((r) => (
                      <td key={r.key} className="px-2 py-2 text-center">
                        <Checkbox
                          checked={!!matrix[r.key]?.[a.key]}
                          disabled={r.locked}
                          onCheckedChange={() => toggle(r.key, a.key)}
                          data-testid={`matrix-cell-${r.key}-${a.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
